import React from "react";
import { SlidersHorizontal, RotateCcw, Check, X } from "lucide-react";
import { Select, Input } from "../ui/Field";
import { cn } from "../../libs/utils";

/**
 * Slide-in filter panel shared by Machine Analytics and Real-Time
 * Analytics — both wireframes show the identical Line / Machine Type /
 * Machine / Date / Shift / Time panel, so it's one component rather than
 * two near-duplicates.
 *
 * Line/Machine/Shift options come from the real master-data APIs
 * (`lineApi`/`machineApi`/`shiftApi`) via props — this component itself
 * stays presentation-only.
 */
const AnalyticsFilterPanel = ({
  open,
  onClose,
  values,
  onChange,
  onApply,
  onReset,
  lineOptions = [],
  machineOptions = [],
  machineTypeOptions = [],
  shiftOptions = [],
  className,
}) => {
  if (!open) return null;

  const set = (key) => (event) => onChange(key, event.target.value);

  return (
    <aside
      className={cn(
        "w-full shrink-0 rounded-xl border border-lightSecondary bg-lightSecondary/40 p-4 sm:w-72",
        className
      )}
    >
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
        <SlidersHorizontal className="h-4 w-4 text-brand" />
        Filter
        <button type="button" aria-label="Close filters" onClick={onClose} className="ml-auto rounded p-1 hover:bg-white"><X className="h-4 w-4" /></button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Line</label>
          <Select aria-label="Line" value={values.line} onChange={set("line")}>
            <option value="">All Line</option>
            {lineOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Machine Type</label>
          <Select aria-label="Machine Type" value={values.machineType} onChange={set("machineType")}>
            <option value="">All Type</option>
            {machineTypeOptions.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Machine</label>
          <Select aria-label="Machine" value={values.machine} onChange={set("machine")}>
            <option value="">All machine</option>
            {machineOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Date</label>
          <Input aria-label="Date" type="date" value={values.date} onChange={set("date")} />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Shift</label>
          <Select aria-label="Shift" value={values.shift} onChange={set("shift")}>
            <option value="">All Shift</option>
            {shiftOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Time</label>
          <div className="flex items-center gap-2">
            <Input aria-label="Start time" type="time" value={values.timeFrom} onChange={set("timeFrom")} />
            <Input aria-label="End time" type="time" value={values.timeTo} onChange={set("timeTo")} />
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-white"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
        <button
          type="button"
          onClick={() => {
            onApply();
            onClose?.();
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brandDark"
        >
          <Check className="h-3.5 w-3.5" />
          Apply Now
        </button>
      </div>
    </aside>
  );
};

export default AnalyticsFilterPanel;
